import { useT } from '@/core/I18nProvider';

import { Bar } from './Frame';
import { useSteppedLoop } from './use-stepped-loop';

import type { ReactElement } from 'react';

/**
 * "Two places, one set of rules" — the widget on the customer's own page, the
 * same tools inside somebody's AI client, and a pulse travelling between them.
 *
 * ## The pulse is the argument
 *
 * Two panels side by side say "we have two integrations". A line with something
 * moving along it says they are the SAME thing, which is the claim: one catalog,
 * one set of approvals, one audit trail, two ways in. Without it this would be a
 * logo strip.
 *
 * The line is hidden below `sm`, where the panels stack vertically and a
 * horizontal connector would point at nothing. The panels still read fine
 * stacked — they just make a weaker version of the point, which is the right
 * trade against drawing a connector that lies about the layout.
 */
const TOOLS = ['upgrade', 'invite', 'export'] as const;

export function SurfacesVisual(): ReactElement {
  const { t } = useT();
  const { ref, step } = useSteppedLoop<HTMLDivElement>(2, 2200);

  return (
    <div ref={ref} aria-hidden="true" data-features-visual className="relative">
      <div className="grid items-stretch gap-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-0">
        {/* ── Your own product. */}
        <div className="relative overflow-hidden rounded-[15px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] p-4 shadow-[0_24px_50px_-40px_rgb(15_23_42/0.4)]">
          <p className="mb-3 font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
            {t('features.showcase.surfaces.yourApp')}
          </p>
          <div className="flex flex-col gap-2.5">
            <Bar w="55%" h={0.55} className="!bg-[color:var(--bd-line-3)]" />
            <Bar w="90%" />
            <Bar w="76%" />
            <Bar w="84%" />
          </div>
          {/* The corner bubble, with a ring that pulses on the beat. */}
          <span className="absolute bottom-3 grid size-9 place-items-center rounded-full text-white end-3">
            <span
              className="absolute inset-0 rounded-full transition-transform duration-700"
              style={{
                background: 'var(--bd-gradient)',
                transform: step === 0 ? 'scale(1)' : 'scale(1.06)',
              }}
            />
            <svg viewBox="0 0 16 16" className="relative size-[15px]" fill="none">
              <path
                d="M2.6 6.4a3.6 3.6 0 0 1 3.6-3.6h3.6a3.6 3.6 0 0 1 0 7.2H6.9L4 12.6V9.7a3.6 3.6 0 0 1-1.4-2.8Z"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        {/* ── The link between them. */}
        <div className="relative hidden w-16 items-center sm:flex">
          <span
            aria-hidden="true"
            className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2"
            style={{
              background:
                'linear-gradient(to inline-end, var(--bd-line-2), rgb(var(--bd-blue-rgb) / 0.5), var(--bd-line-2))',
            }}
          />
          <span
            aria-hidden="true"
            className="absolute top-1/2 size-[7px] -translate-y-1/2 rounded-full sx-pulse"
            style={{ background: 'var(--bd-blue)' }}
          />
        </div>

        {/* ── Their AI client. Dark, because every one of them is. */}
        <div className="overflow-hidden rounded-[15px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-band)] p-4 shadow-[0_24px_50px_-40px_rgb(15_23_42/0.4)]">
          <p className="mb-3 font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.14em] text-[color:var(--bd-band-dim)] rtl:normal-case rtl:tracking-normal">
            {t('features.showcase.surfaces.theirTools')}
          </p>
          <ul className="flex flex-col gap-1.5">
            {TOOLS.map((id) => (
              <li
                key={id}
                className="flex items-center gap-2 rounded-[7px] bg-white/[0.06] px-2.5 py-2 text-[0.78rem] text-[color:var(--bd-band-ink)]"
              >
                <span
                  className="size-[6px] flex-none rounded-full"
                  style={{ background: 'var(--bd-cyan)' }}
                />
                <span className="truncate">{t(`features.showcase.surfaces.tool.${id}`)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 font-mono rtl:font-sans text-[0.68rem] text-[color:var(--bd-band-dim)]">
            {t('features.showcase.surfaces.clients')}
          </p>
        </div>
      </div>
    </div>
  );
}
